import type {
	NoteStreamingChunk,
	Note,
	NoteBlock
} from "@vita-learning/types";
import type {
	BulletedListElement,
	ListItemChildElement,
	ListItemElement,
	NumberedListElement,
} from "@vita-learning/types/dist/lib/plate";

const typeRegex = new RegExp(/^prediction.content\[(\d+)\]\.type$/);
const childrenRegex = new RegExp(/^prediction.content\[(\d+)\]\.children$/);
const textRegex = new RegExp(
	/^prediction.content\[(\d+)\]\.children\[(\d+)\]\.text$/
);
const flagRegex = new RegExp(
	/^prediction.content\[(\d+)\]\.children\[(\d+)\]\.\w+$/
);
const listTypeRegex = new RegExp(
	/^prediction.content\[(\d+)\]\.children\[(\d+)\]\.type$/
);
const listChildrenRegex = new RegExp(
	/^prediction.content\[(\d+)\]\.children\[(\d+)\]\.children$/
);

const listItemTypeRegex = new RegExp(
	/^prediction.content\[(\d+)\]\.children\[(\d+)\]\.children\[(\d+)\]\.type$/
);
const listItemChildrenRegex = new RegExp(
	/^prediction.content\[(\d+)\]\.children\[(\d+)\]\.children\[(\d+)\]\.children$/
);
const listItemTextRegex = new RegExp(
	/^prediction.content\[(\d+)\]\.children\[(\d+)\]\.children\[(\d+)\]\.children\[(\d+)\]\.text$/
);
const listItemFlagRegex = new RegExp(
	/^prediction.content\[(\d+)\]\.children\[(\d+)\]\.children\[(\d+)\]\.children\[(\d+)\]\.\w+$/
);
const imgPropRegex = new RegExp(
	/^prediction\.content\[(\d+)\]\.(id|url|expiresAt)$/
);

export const notePredictionStreamingReducer = (
	note: Note,
	chunk: NoteStreamingChunk
): Note => {
	const typeElementMatch = typeRegex.exec(chunk.key);
	const childrenElementMatch = childrenRegex.exec(chunk.key);
	const textElementMatch = textRegex.exec(chunk.key);
	const flagElementMatch = flagRegex.exec(chunk.key);
	const listTypeElementMatch = listTypeRegex.exec(chunk.key);
	const listChildrenElementMatch = listChildrenRegex.exec(chunk.key);
	const listItemTypeElementMatch = listItemTypeRegex.exec(chunk.key);
	const listItemChildrenElementMatch = listItemChildrenRegex.exec(chunk.key);
	const listItemTextElementMatch = listItemTextRegex.exec(chunk.key);
	const listItemFlagElementMatch = listItemFlagRegex.exec(chunk.key);
	const imgPropMatch = imgPropRegex.exec(chunk.key);

	// NOTE: Must get checked before listItemFlagElementMatch
	if (
		listItemTextElementMatch?.[1] &&
		listItemTextElementMatch[2] &&
		listItemTextElementMatch[3] &&
		listItemTextElementMatch[4]
	) {
		const blockIndex = parseInt(listItemTextElementMatch[1], 10);
		const listIndex = parseInt(listItemTextElementMatch[2], 10);
		const listItemIndex = parseInt(listItemTextElementMatch[3], 10);
		const nodeIndex = parseInt(listItemTextElementMatch[4], 10);

		const delta = chunk.value as string;

		const content = note.content?.map((block, i) =>
			i !== blockIndex
				? block
				: ({
						...block,
						// type: "ul",
						children: block.children.map((list, j) => {
							const child_ = { ...list } as ListItemElement;

							if (j !== listIndex) return child_;

							return {
								...child_,
								// type: "li",
								children: child_.children.map((listItem, k) => {
									const listItem_ = { ...listItem } as NoteBlock;

									if (k !== listItemIndex) return listItem_;

									return {
										// type: "lic",
										...listItem_,
										children: listItem_.children.map((node, n) =>
											n !== nodeIndex
												? node
												: {
														...node,
														text:
															chunk.method === "set"
																? delta
																: chunk.method === "concat"
																	? node.text + delta
																	: node.text,
													}
										),
									};
								}),
							};
						}),
					} as NoteBlock)
		);

		return {
			...note,
			content,
		};
	}

	if (
		listItemFlagElementMatch?.[1] &&
		listItemFlagElementMatch[2] &&
		listItemFlagElementMatch[3] &&
		listItemFlagElementMatch[4] &&
		chunk.method === "set"
	) {
		const blockIndex = parseInt(listItemFlagElementMatch[1], 10);
		const listIndex = parseInt(listItemFlagElementMatch[2], 10);
		const listItemIndex = parseInt(listItemFlagElementMatch[3], 10);
		const nodeIndex = parseInt(listItemFlagElementMatch[4], 10);

		const delta = chunk.value as boolean;

		const content = note.content?.map((block, i) =>
			i !== blockIndex
				? block
				: ({
						...block,
						children: block.children.map((list, j) => {
							const child_ = { ...list } as ListItemElement;

							if (j !== listIndex) return child_;

							return {
								...child_,
								children: child_.children.map((listItem, k) => {
									const listItem_ = { ...listItem } as NoteBlock;

									if (k !== listItemIndex) return listItem_;

									return {
										...listItem_,
										children: listItem_.children.map((node, n) =>
											n !== nodeIndex
												? node
												: {
														...node,
														[chunk.key.split(".")[5] as string]: delta,
													}
										),
									};
								}),
							};
						}),
					} as NoteBlock)
		);

		return {
			...note,
			content,
		};
	}

	if (
		listItemTypeElementMatch?.[1] &&
		listItemTypeElementMatch[2] &&
		listItemTypeElementMatch[3] &&
		chunk.method === "set"
	) {
		const index = parseInt(listItemTypeElementMatch[1], 10);
		const childIndex = parseInt(listItemTypeElementMatch[2], 10);
		const listItemChildIndex = parseInt(listItemTypeElementMatch[3], 10);

		const delta = chunk.value as "lic";

		const content = note.content?.map((con, i) =>
			i !== index
				? con
				: ({
						...con,
						children: con.children.map((child, j) => {
							const child_ = { ...child } as ListItemElement;

							if (j !== childIndex) return child_;

							return {
								...child_,
								children: child_.children.map((listItem, k) => {
									if (k !== listItemChildIndex) return listItem;

									return {
										...listItem,
										type: delta,
										children:
											"children" in listItem &&
											Array.isArray(listItem.children) &&
											listItem.children.length > 0
												? listItem.children
												: [{ text: "" }],
									};
								}),
							};
						}),
					} as NoteBlock)
		);

		return {
			...note,
			content,
		};
	}

	if (
		listItemChildrenElementMatch?.[1] &&
		listItemChildrenElementMatch[2] &&
		listItemChildrenElementMatch[3]
	) {
		const index = parseInt(listItemChildrenElementMatch[1], 10);
		const childIndex = parseInt(listItemChildrenElementMatch[2], 10);
		const listItemChildIndex = parseInt(listItemChildrenElementMatch[3], 10);

		const delta = chunk.value as NoteBlock["children"];

		const content = note.content?.map((con, i) =>
			i !== index
				? con
				: ({
						...con,
						children: con.children.map((child, j) => {
							const child_ = { ...child } as ListItemElement;

							if (j !== childIndex) return child_;

							return {
								...child_,
								children: child_.children.map((listItem, k) => {
									const listItem_ = { ...listItem } as NoteBlock;

									if (k !== listItemChildIndex) return listItem_;

									if (chunk.method === "set")
										return {
											...listItem_,
											children: delta.length > 0 ? delta : [{ text: "" }],
										};

									if (chunk.method === "push") {
										// Remove the last element immutably if it's a placeholder
										let newChildren = listItem_.children;
										if (
											listItem_.children.length === 1 &&
											listItem_.children[0]?.text === ""
										) {
											newChildren = listItem_.children.slice(0, -1);
										}

										return {
											...listItem_,
											children: [
												...newChildren,
												"text" in delta ? delta : { ...delta, text: "" },
											],
										};
									}

									return listItem;
								}),
							};
						}),
					} as NoteBlock)
		);

		return {
			...note,
			content,
		};
	}

	if (listChildrenElementMatch?.[1] && listChildrenElementMatch[2]) {
		const index = parseInt(listChildrenElementMatch[1], 10);
		const childIndex = parseInt(listChildrenElementMatch[2], 10);

		const delta = chunk.value as NoteBlock["children"];

		const content = note.content?.map((con, i) =>
			i !== index
				? con
				: ({
						...con,
						children: con.children.map((child, j) => {
							const child_ = { ...child } as ListItemElement;

							if (j !== childIndex) return child_;

							if (chunk.method === "set")
								return {
									...child_,
									children:
										delta.length > 0 && Object.keys(delta[0]).length > 1
											? delta
											: [{ type: "lic", children: [{ text: "" }] }],
								};

							if (chunk.method === "push") {
								// Remove the last element immutably if it's a placeholder
								let newChildren = child_.children;
								if (
									child_.children.length === 1 &&
									child_.children[0]?.type === "lic" &&
									child_.children[0]?.children?.[0]?.text === ""
								) {
									newChildren = child_.children.slice(0, -1) as [
										ListItemChildElement,
										...(BulletedListElement | NumberedListElement)[],
									];
								}

								return {
									...child,
									children: [
										...newChildren,
										Object.keys(delta).length > 0
											? delta
											: { type: "lic", children: [{ text: "" }] },
									],
								};
							}

							return child;
						}),
					} as NoteBlock)
		);

		return {
			...note,
			content,
		};
	}

	if (
		listTypeElementMatch?.[1] &&
		listTypeElementMatch[2] &&
		chunk.method === "set"
	) {
		const index = parseInt(listTypeElementMatch[1], 10);
		const childIndex = parseInt(listTypeElementMatch[2], 10);

		const delta = chunk.value as "li";

		const content = note.content?.map((con, i) =>
			i !== index
				? con
				: ({
						...con,
						children: con.children.map((child, j) => {
							if (j !== childIndex) return child;

							return {
								...child,
								type: delta,
								children:
									"children" in child &&
									Array.isArray(child.children) &&
									child.children.length > 0
										? child.children
										: [{ type: "lic", children: [{ text: "" }] }],
							};
						}),
					} as NoteBlock)
		);

		return {
			...note,
			content,
		};
	}

	// NOTE: Must get checked before flagElementMatch
	if (textElementMatch?.[1] && textElementMatch[2]) {
		const index = parseInt(textElementMatch[1], 10);
		const childIndex = parseInt(textElementMatch[2], 10);

		const delta = chunk.value as string;

		const content = note.content?.map((con, i) =>
			i !== index
				? con
				: ({
						...con,
						children: con.children.map((child, j) =>
							j !== childIndex
								? child
								: {
										...child,
										text:
											chunk.method === "set"
												? delta
												: chunk.method === "concat"
													? child.text + delta
													: child.text,
									}
						),
					} as NoteBlock)
		);

		return {
			...note,
			content,
		};
	}

	if (flagElementMatch?.[1] && flagElementMatch[2] && chunk.method === "set") {
		const index = parseInt(flagElementMatch[1], 10);
		const childIndex = parseInt(flagElementMatch[2], 10);

		const delta = chunk.value as boolean;

		const content = note.content?.map((con, i) =>
			i !== index
				? con
				: ({
						...con,
						children: con.children.map((child, j) =>
							j !== childIndex
								? child
								: {
										...child,
										[chunk.key.split(".").at(-1) as string]: delta,
									}
						),
					} as NoteBlock)
		);

		return {
			...note,
			content,
		};
	}

	if (childrenElementMatch?.[1]) {
		const index = parseInt(childrenElementMatch[1], 10);

		const delta = chunk.value as NoteBlock["children"];

		const content = note.content?.map((block, i) => {
			if (i !== index) return block;

			if (chunk.method === "set") {
				if (block.type === "ul" || block.type === "ol")
					return {
						...block,
						children:
							delta.length > 0
								? delta
								: [
										{
											type: "li",
											children: [{ type: "lic", children: [{ text: "" }] }],
										},
									],
					} as NoteBlock;

				return {
					...block,
					children: delta.length > 0 ? delta : [{ text: "" }],
				} as NoteBlock;
			}

			if (chunk.method === "push") {
				if (block.type === "ul" || block.type === "ol") {
					const block_ = { ...block } as
						| BulletedListElement
						| NumberedListElement;
					// Remove the last element immutably if it's a placeholder
					let newChildren = block_.children;
					if (
						block_.children.length === 1 &&
						block_.children[0]?.children?.[0]?.children?.[0]?.text === ""
					) {
						newChildren = block_.children.slice(0, -1);
					}
					return {
						...block_,
						children: [
							...newChildren,
							{
								type: "li",
								children: [
									{
										type: "lic",
										children: [
											"text" in delta ? delta : { ...delta, text: "" },
										],
									},
								],
							},
						],
					} as NoteBlock;
				}

				// Remove the last element immutably if it's a placeholder
				let newChildren = block.children;
				if (block.children.length === 1 && block.children[0]?.text === "") {
					newChildren = block.children.slice(0, -1);
				}
				return {
					...block,
					children: [
						...newChildren,
						"text" in delta ? delta : { ...delta, text: "" },
					],
				} as NoteBlock;
			}

			return block;
		});

		return {
			...note,
			content,
		};
	}

	if (typeElementMatch?.[1] && chunk.method === "set") {
		const index = parseInt(typeElementMatch[1], 10);

		const delta = chunk.value as NoteBlock["type"];

		const content = note.content?.map((block, i) => {
			if (i !== index) return block;

			if (delta === "ul" || delta === "ol")
				return {
					...block,
					type: delta,
					children:
						block.type === delta && block.children.length > 0
							? block.children
							: [
									{
										type: "li",
										children: [{ type: "lic", children: [{ text: "" }] }],
									},
								],
				} as NoteBlock;

			return {
				...block,
				type: delta,
				// Provide fallback while streaming so udecode editor doesn't crash
				children: block.children.length > 0 ? block.children : [{ text: "" }],
			} as NoteBlock;
		});

		return {
			...note,
			content,
		};
	}

	if (imgPropMatch?.[1] && imgPropMatch[2] && chunk.method === "set") {
		const index = parseInt(imgPropMatch[1], 10);
		const prop = imgPropMatch[2];
		const delta = chunk.value as string;

		const content = note.content?.map((block, i) =>
			i !== index
				? block
				: ({
						...block,
						[prop]: delta,
					} as NoteBlock)
		);

		return {
			...note,
			content,
		};
	}

	switch (chunk.key) {
		case "prediction": {
			if (chunk.method !== "set") return note;
			return {
				...note,
				...chunk.value,
			};
		}
		case "prediction.id": {
			if (chunk.method !== "set") return note;
			return {
				...note,
				id: chunk.value,
			};
		}
		case "prediction.content": {
			if (chunk.method === "set")
				return {
					...note,
					content:
						chunk.value.length > 0
							? (chunk.value as NoteBlock[])
							: [{ type: "p", children: [{ text: "" }] }],
				};

			if (chunk.method === "push") {
				// Remove the last element immutably if it's a placeholder
				let newContent = note.content;
				if (
					note.content?.length === 1 &&
					note.content[0]?.children[0]?.text === ""
				) {
					newContent = note.content.slice(0, -1);
				}

				return {
					...note,
					content: [
						...(newContent || []),
						Object.keys(chunk.value).length > 0
							? (chunk.value as NoteBlock)
							: { type: "p", children: [{ text: "" }] },
					],
				};
			}

			return note;
		}
		case "prediction.userId": {
			if (chunk.method !== "set") return note;
			return {
				...note,
				userId: chunk.value,
			};
		}
		case "prediction.title": {
			if (chunk.method === "set")
				return {
					...note,
					title: chunk.value,
				};

			if (chunk.method === "concat")
				return {
					...note,
					title: note.title + chunk.value,
				};

			return note;
		}
		case "prediction.meta": {
			if (chunk.method !== "set") return note;

			// Set temp timestamps
			const timestamp = new Date().toISOString();

			return {
				...note,
				meta: {
					createdAt: timestamp,
					updatedAt: timestamp,
					version: 0,
				},
			};
		}
		case "prediction.meta.createdAt": {
			if (chunk.method !== "set") return note;
			return {
				...note,
				meta: {
					...note.meta,
					createdAt: chunk.value,
				},
			};
		}
		case "prediction.meta.updatedAt": {
			if (chunk.method !== "set") return note;
			return {
				...note,
				meta: {
					...note.meta,
					updatedAt: chunk.value,
				},
			};
		}
		case "prediction.meta.version": {
			if (chunk.method !== "set") return note;
			return {
				...note,
				meta: {
					...note.meta,
					version: chunk.value,
				},
			};
		}
		case "prediction.source": {
			if (chunk.method !== "set") return note;
			return {
				...note,
				source: chunk.value,
			} as Note; // Cast to Note ok because the associated props will be streamed in a separate chunk
		}
		case "prediction.fileId": {
			if (chunk.method !== "set") return note;
			return {
				...note,
				source: "file",
				fileId: chunk.value,
			};
		}
		case "prediction.url": {
			if (chunk.method !== "set") return note;
			return {
				...note,
				source: "url",
				url: chunk.value,
			};
		}
		case "prediction.prompt": {
			if (chunk.method !== "set") return note;
			return {
				...note,
				source: "prompt",
				prompt: chunk.value,
			};
		}
		default:
			return note;
	}
};
